/**
 * FabHide — 고정 플로팅 버튼(FAB)을 "오른쪽으로 밀어 숨기기" 가능하게 만드는 재사용 모듈.
 *
 * 왜: 화면캡쳐를 자주 하는 회원들이 FAB 이 화면을 가려 불편하다고 함. 캡쳐 순간에만 치울 수
 * 있으면 되므로, 제스처로 치우고 오른쪽 가장자리에 남는 얇은 손잡이로 되살리는 방식.
 *
 * 숨김 3단계 (LV)
 *   0 보임      기본
 *   1 손잡이    FAB 을 오른쪽으로 밀면(60px+) → 오른쪽 끝 16px 손잡이만 남음. 손잡이 탭하면 복귀.
 *   2 완전숨김  손잡이까지 사라짐 — 캡쳐에 아무것도 안 걸린다.
 *               · FAB 을 한 번에 멀리(160px+) 밀거나
 *               · 남은 손잡이를 다시 오른쪽으로 밀면
 *               복귀 = 오른쪽 가장자리에서 왼쪽으로 스와이프(또는 페이지 다시 열기).
 *
 * 기억: localStorage 에는 0/1 만 저장한다(완전숨김은 그 화면에서만). 페이지를 다시 열면
 * 최소한 손잡이는 보이므로 버튼을 영영 잃어버리는 일이 없다.
 *
 * 사용:
 *   $j.Ui.FabHide.attach('#ai-bal-fab', { key: 'ai_bal', tint: '#1a73e8' });
 *   const ctl = $j.Ui.FabHide.attach(el, {...});  // { hide, hideAll, show, level, sync, destroy }
 *
 * opts
 *   key       localStorage 키 접미사. 주면 숨김상태를 기기에 기억(페이지 재진입해도 유지).
 *   tint      손잡이 색 (기본 회색). FAB 색과 맞추면 자연스럽다.
 *   threshold 손잡이 단계로 판정할 가로 이동거리(px, 기본 60)
 *   fullPx    완전숨김으로 판정할 가로 이동거리(px, 기본 160)
 *
 * 동작 메모
 * - display 는 건드리지 않는다(transform + opacity + pointer-events). 레거시 스크립트가
 *   display 로 FAB 을 켜고 끄는 페이지(일지 탭 전환 등)와 충돌하지 않게 하기 위함.
 *   대신 FAB 이 display:none 이면 손잡이도 같이 감춘다(MutationObserver 로 추적).
 * - 드래그 뒤 이어지는 click 은 document 캡쳐단계에서 삼킨다. 인라인 onclick(레거시 마크업)은
 *   대상 엘리먼트에 먼저 등록돼 있어 엘리먼트 리스너로는 막을 수 없다.
 */
const TAB_H = 44;   // 손잡이 높이(px) — scss .fab-hide-tab 과 맞춘다

const LV_SHOW = 0;
const LV_TAB  = 1;
const LV_FULL = 2;

const EDGE_ZONE = 24;   // 복귀 스와이프를 받는 오른쪽 가장자리 폭(px)
const EDGE_PULL = 40;   // 가장자리에서 이만큼 왼쪽으로 끌면 복귀(px)

export default class FabHide {
    attach(target, opts = {}) {
        const fab = typeof target === 'string' ? document.querySelector(target) : target;
        if (!fab || fab.dataset.fabHide) return null;
        fab.dataset.fabHide = '1';

        const storeKey  = opts.key ? `fabhide_${opts.key}` : '';
        const threshold = opts.threshold || 60;
        const fullPx    = opts.fullPx || 160;

        // ── 되살리기 손잡이 — 화면 오른쪽 끝에 얇게. 캡쳐를 거의 방해하지 않는 크기.
        const tab = document.createElement('button');
        tab.type      = 'button';
        tab.className = 'fab-hide-tab';
        tab.textContent = '‹';
        tab.setAttribute('aria-label', '숨긴 버튼 다시 보기');
        if (opts.tint) tab.style.background = opts.tint;
        document.body.appendChild(tab);

        let level = LV_SHOW;

        // FAB 이 display:none 이면(레거시 탭 전환) 손잡이도 숨긴다.
        const isFabVisible = () => getComputedStyle(fab).display !== 'none';

        // 손잡이를 FAB 과 같은 높이에 맞춘다. FAB 의 bottom 이 스크롤에 따라 움직이는 페이지 대응.
        const sync = () => {
            const bottom = parseFloat(getComputedStyle(fab).bottom) || 0;
            const h      = fab.offsetHeight || TAB_H;
            tab.style.bottom  = (bottom + Math.max(0, (h - TAB_H) / 2)) + 'px';
            tab.style.display = (level === LV_TAB && isFabVisible()) ? 'block' : 'none';
        };

        const setLevel = (lv, remember = true) => {
            level = lv;
            // 완전숨김은 저장하지 않는다 — 재진입 시 손잡이는 보이게 해 되찾을 길을 남긴다.
            if (remember && storeKey) {
                $j.Common.Store.local.set(storeKey, lv === LV_SHOW ? '0' : '1'); // Store 가 try/catch 내장(시크릿모드 등)
            }
            fab.style.transition    = 'transform .2s ease, opacity .2s ease';
            fab.style.transform     = lv === LV_SHOW ? 'translateX(0)' : 'translateX(130%)';
            fab.style.opacity       = lv === LV_SHOW ? '1' : '0';
            fab.style.pointerEvents = lv === LV_SHOW ? 'auto' : 'none';
            sync();
        };

        // ── 가로 드래그 (FAB / 손잡이 공통) ──
        fab.style.touchAction = 'pan-y';  // 세로 스크롤은 그대로 살린다
        tab.style.touchAction = 'pan-y';
        let startX = 0, startY = 0, dragging = null, moved = false;

        const onDown = (e) => {
            if (level === LV_FULL) return;
            if (e.currentTarget === fab && level !== LV_SHOW) return;
            if (e.currentTarget === tab && level !== LV_TAB) return;
            startX   = e.clientX;
            startY   = e.clientY;
            dragging = e.currentTarget;
            moved    = false;
            fab.style.transition = 'none';
            dragging.setPointerCapture?.(e.pointerId);
        };
        const onMove = (e) => {
            if (!dragging) return;
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            // 가로 의도가 분명해질 때까지 무시 — 세로 스크롤 오인 방지
            if (!moved && (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy))) return;
            moved = true;
            const px = Math.max(0, dx);
            if (dragging === fab) {
                fab.style.transform = `translateX(${px}px)`;
                fab.style.opacity   = String(Math.max(0.3, 1 - px / 160));
            } else {
                tab.style.opacity = String(Math.max(0, 0.4 - px / 80));  // 손잡이는 밀수록 옅어짐
            }
        };
        const onUp = (e) => {
            if (!dragging) return;
            const from = dragging;
            dragging   = null;
            const dx   = e.clientX - startX;

            if (from === tab) {
                tab.style.opacity = '';                               // scss 기본값(.4)으로 복귀
                if (moved && dx > threshold) setLevel(LV_FULL);       // 손잡이까지 치움
                return;
            }
            if (!moved || dx <= threshold) setLevel(LV_SHOW, moved);  // 덜 밀었으면 제자리
            else setLevel(dx > fullPx ? LV_FULL : LV_TAB);            // 멀리 밀면 한 번에 완전숨김
        };

        // 드래그였던 제스처의 click 은 삼킨다(인라인 onclick 포함).
        const onClickCapture = (e) => {
            if (!moved || !fab.contains(e.target)) return;
            moved = false;
            e.stopPropagation();
            e.preventDefault();
        };

        // ── 완전숨김 복귀 — 오른쪽 가장자리에서 왼쪽으로 스와이프 ──
        let edgeX = null, edgeY = 0;
        const onEdgeDown = (e) => {
            edgeX = (level === LV_FULL && e.clientX > window.innerWidth - EDGE_ZONE) ? e.clientX : null;
            edgeY = e.clientY;
        };
        const onEdgeMove = (e) => {
            if (edgeX === null) return;
            const dx = e.clientX - edgeX;
            if (dx < -EDGE_PULL && Math.abs(dx) > Math.abs(e.clientY - edgeY)) {
                edgeX = null;
                setLevel(LV_SHOW);
            }
        };
        const onEdgeUp = () => { edgeX = null; };

        fab.addEventListener('pointerdown', onDown);
        fab.addEventListener('pointermove', onMove);
        fab.addEventListener('pointerup', onUp);
        fab.addEventListener('pointercancel', onUp);
        tab.addEventListener('pointerdown', onDown);
        tab.addEventListener('pointermove', onMove);
        tab.addEventListener('pointerup', onUp);
        tab.addEventListener('pointercancel', onUp);
        tab.addEventListener('click', () => { if (!moved) setLevel(LV_SHOW); });
        document.addEventListener('click', onClickCapture, true);
        document.addEventListener('pointerdown', onEdgeDown, true);
        document.addEventListener('pointermove', onEdgeMove, true);
        document.addEventListener('pointerup', onEdgeUp, true);
        document.addEventListener('pointercancel', onEdgeUp, true);  // 스크롤 시작으로 취소된 경우

        const onScroll = () => sync();
        document.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onScroll);

        // 레거시가 style/class 로 FAB 을 켜고 끄면 손잡이도 따라가게
        const mo = new MutationObserver(sync);
        mo.observe(fab, { attributes: true, attributeFilter: ['style', 'class'] });

        let remembered = false;
        if (storeKey) {
            remembered = $j.Common.Store.local.get(storeKey) === '1';
        }
        setLevel(remembered ? LV_TAB : LV_SHOW, false);

        return {
            hide:    () => setLevel(LV_TAB),
            hideAll: () => setLevel(LV_FULL),
            show:    () => setLevel(LV_SHOW),
            level:   () => level,
            sync,
            destroy() {
                mo.disconnect();
                fab.removeEventListener('pointerdown', onDown);
                fab.removeEventListener('pointermove', onMove);
                fab.removeEventListener('pointerup', onUp);
                fab.removeEventListener('pointercancel', onUp);
                document.removeEventListener('click', onClickCapture, true);
                document.removeEventListener('pointerdown', onEdgeDown, true);
                document.removeEventListener('pointermove', onEdgeMove, true);
                document.removeEventListener('pointerup', onEdgeUp, true);
                document.removeEventListener('pointercancel', onEdgeUp, true);
                document.removeEventListener('scroll', onScroll, true);
                window.removeEventListener('resize', onScroll);
                tab.remove();
                delete fab.dataset.fabHide;
            },
        };
    }
}
